import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Sparkles, MessageCircle } from 'lucide-react';
import { KnowledgeSelector } from '../KnowledgeSelector';
import { InfoResearchChat } from '../InfoResearchChat';

export interface WeekGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (additionalInfo: string, enableDeepResearch?: boolean, reelsTemplateId?: string, youtubeUrl?: string) => void;
  isGenerating: boolean;
}

export const WeekGenerationModal: React.FC<WeekGenerationModalProps> = ({ 
    isOpen, 
    onClose, 
    onGenerate, 
    isGenerating 
  }) => {
    const [additionalInfo, setAdditionalInfo] = useState('');
    const [enableDeepResearch, setEnableDeepResearch] = useState(true);
    const [selectedReelsTemplate, setSelectedReelsTemplate] = useState<{ id: string; name: string } | null>(null);
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [contentSource, setContentSource] = useState<'text' | 'reels' | 'youtube' | 'infopovody' | 'none'>('none');
    const [showInfoResearchChat, setShowInfoResearchChat] = useState(false);
    const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
    
    // Создаём корневой элемент для портала
    useEffect(() => {
      let root = document.getElementById('modal-root');
      if (!root) {
        root = document.createElement('div');
        root.id = 'modal-root';
        document.body.appendChild(root);
      }
      setModalRoot(root);
      
      return () => {
        if (root && !root.hasChildNodes() && root.parentElement) {
          root.parentElement.removeChild(root);
        }
      };
    }, []);
  
    // Блокировка прокрутки при открытом модальном окне
    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = 'unset';
        };
      }
    }, [isOpen]);
  
    // Сброс полей при закрытии
    useEffect(() => {
      if (!isOpen) {
        setAdditionalInfo('');
        setEnableDeepResearch(true);
        setSelectedReelsTemplate(null);
        setYoutubeUrl('');
        setContentSource('none');
        setShowInfoResearchChat(false);
      }
    }, [isOpen]);
  
    // Функция для автоматического изменения высоты textarea
    const autoResizeTextarea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const textarea = e.target;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    };
  
    // Функция валидации YouTube URL
    const validateYouTubeUrl = (url: string): boolean => {
      const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/;
      return youtubeRegex.test(url);
    };
  
    const handleGenerate = () => {
      const finalYoutubeUrl = contentSource === 'youtube' ? youtubeUrl : undefined;
      const finalReelsTemplateId = contentSource === 'reels' ? selectedReelsTemplate?.id : undefined;
      const finalAdditionalInfo = contentSource === 'text' ? additionalInfo : '';
      
      onGenerate(finalAdditionalInfo, enableDeepResearch, finalReelsTemplateId, finalYoutubeUrl);
    };
  
    if (!isOpen || !modalRoot) return null;
  
    const modalContent = (
      <div 
        className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-0 sm:p-4 backdrop-blur-sm"
        onClick={onClose}
      >
        <div 
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full sm:max-w-4xl sm:mx-auto h-[100dvh] sm:h-[80vh] flex flex-col overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Заголовок */}
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 dark:text-white">
              Генерация новой недели
            </h3>
            <button 
              onClick={onClose}
              disabled={isGenerating}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors disabled:opacity-50 p-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
  
          {/* Контент */}
          <div className="p-4 sm:p-6 lg:p-8 space-y-6 lg:space-y-8 overflow-y-auto flex-1">
            {/* Переключатель источников контента */}
            <div>
              
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
                {[

                  { value: 'none', label: 'Без источника' },
                  { value: 'text', label: 'Текст' },
                  { value: 'reels', label: 'Файл рилсов' },
                  { value: 'youtube', label: 'YouTube' },
                  { value: 'infopovody', label: 'Инфоповоды' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setContentSource(option.value as any)}
                    disabled={
                      isGenerating || option.value === 'youtube' || option.value === 'infopovody'
                    }
                    aria-disabled={option.value === 'youtube' || option.value === 'infopovody'}
                    title={
                      option.value === 'youtube' || option.value === 'infopovody'
                        ? 'Вкладка временно недоступна'
                        : undefined
                    }
                    className={`flex items-center justify-center p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 ${
                      contentSource === option.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <span className="text-xs sm:text-sm font-medium text-center">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
  
            {/* Контент в зависимости от выбранного источника */}
            {contentSource === 'text' && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Опишите новые события, достижения, инфоповоды или любую другую информацию, которую нужно учесть при создании новой недели контент-плана.
                </p>
                <textarea
                  value={additionalInfo}
                  onChange={(e) => {
                    setAdditionalInfo(e.target.value);
                    autoResizeTextarea(e);
                  }}
                  placeholder="Ключевые события недели
Важные новости или отзывы"
                  className="w-full h-32 sm:h-40 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-none text-sm sm:text-base leading-relaxed"
                  disabled={isGenerating}
                  style={{ minHeight: '120px' }}
                />
              </div>
            )}
  
            {contentSource === 'reels' && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Выберите файл типа Reels, чтобы использовать его как референс/шаблон при генерации недели.
                </p>
                <div className="max-w-xl">
                  <KnowledgeSelector
                    onSelect={(item) => setSelectedReelsTemplate(item ? { id: item.id, name: item.name } : null)}
                    placeholder="Выберите файл Reels"
                    fileTypes={["reels"]}
                  />
                  {selectedReelsTemplate && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      Выбрано: <span className="font-medium">{selectedReelsTemplate.name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
  
            {contentSource === 'youtube' && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Вставьте ссылку на YouTube видео, которое будет использовано как основа для генерации контента.
                </p>
                <div className="max-w-xl">
                  <input
                    type="url"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
                    disabled={isGenerating}
                  />
                  {youtubeUrl && !validateYouTubeUrl(youtubeUrl) && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      Пожалуйста, введите корректную ссылку на YouTube
                    </p>
                  )}
                  {youtubeUrl && validateYouTubeUrl(youtubeUrl) && (
                    <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                      ✓ Корректная ссылка на YouTube
                    </p>
                  )}
                </div>
              </div>
            )}
  
            {contentSource === 'infopovody' && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Используйте чат для поиска актуальных инфоповодов, трендов и событий в вашей сфере.
                </p>
                <div className="max-w-xl">
                  <button
                    onClick={() => setShowInfoResearchChat(true)}
                    disabled={isGenerating}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Открыть чат для поиска инфоповодов
                  </button>
                </div>
              </div>
            )}
  
            {contentSource === 'none' && (
              <div className="text-center py-8">
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Генерация без дополнительных источников
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Контент будет сгенерирован на основе существующих данных и настроек контент-плана
                </p>
              </div>
            )}
          </div>
  
          {/* Кнопки */}
          <div className="sticky bottom-0 left-0 right-0 flex flex-row justify-end space-x-4 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur z-[10000]">
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Отмена
            </button>
            <button
              onClick={handleGenerate}
              disabled={
                isGenerating || 
                (contentSource === 'youtube' && youtubeUrl && !validateYouTubeUrl(youtubeUrl)) ||
                (contentSource === 'reels' && !selectedReelsTemplate) ||
                (contentSource === 'text' && !additionalInfo.trim())
              }
              className="px-6 sm:px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Генерация...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Начать
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  
    return ReactDOM.createPortal(
      <>
        {modalContent}
        <InfoResearchChat 
          isOpen={showInfoResearchChat} 
          onClose={() => setShowInfoResearchChat(false)} 
        />
      </>, 
      modalRoot
    );
  };;


